import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getAdminUsers } from '../../api/enrollments';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

export default function AdminUsersPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', search, page],
    queryFn: () => getAdminUsers({ search: search || undefined, page, limit: 20 }),
    keepPreviousData: true,
  } as any);

  const users = (data as any)?.users ?? [];
  const pagination = (data as any)?.pagination;

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  }, []);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Iscritti</h1>
      </div>

      <div className="filter-bar">
        <input
          type="text"
          className="form-control"
          placeholder="Cerca per nome, cognome, email, azienda..."
          value={search}
          onChange={handleSearchChange}
          style={{ minWidth: '280px' }}
        />
        {search && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => { setSearch(''); setPage(1); }}
          >
            Azzera
          </button>
        )}
      </div>

      <div className="card" style={{ padding: 0 }}>
        {isLoading ? (
          <div className="loading-container">
            <div className="spinner" />
          </div>
        ) : users.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--gray-500)' }}>
            Nessun iscritto trovato
          </div>
        ) : (
          <>
            <div className="table-container" style={{ borderRadius: 0, border: 'none' }}>
              <table>
                <thead>
                  <tr>
                    <th>Nominativo</th>
                    <th>Azienda</th>
                    <th>Iscrizioni attive</th>
                    <th>Totale iscrizioni</th>
                    <th>Registrato il</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user: any) => (
                    <tr key={user.id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>
                          {user.nome} {user.cognome}
                        </div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--gray-400)' }}>
                          {user.email}
                        </div>
                      </td>
                      <td style={{ color: 'var(--gray-600)' }}>
                        {user.azienda ?? '—'}
                      </td>
                      <td>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '1.75rem',
                            height: '1.75rem',
                            borderRadius: '999px',
                            background: user.active_enrollment_count > 0 ? 'var(--primary)' : 'var(--gray-200)',
                            color: user.active_enrollment_count > 0 ? '#fff' : 'var(--gray-500)',
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            padding: '0 0.5rem',
                          }}
                        >
                          {user.active_enrollment_count}
                        </span>
                      </td>
                      <td style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>
                        {user.enrollment_count}
                      </td>
                      <td style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                        {format(new Date(user.created_at), 'd MMM yyyy', { locale: it })}
                      </td>
                      <td>
                        <Link
                          to={`/admin/users/${user.id}`}
                          className="btn btn-ghost btn-sm"
                        >
                          Dettaglio
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination && pagination.total > 20 && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1rem 1.5rem',
                  borderTop: '1px solid var(--gray-100)',
                }}
              >
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                  {pagination.total} iscritti totali
                </span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Precedente
                  </button>
                  <span
                    style={{
                      padding: '0.25rem 0.75rem',
                      fontSize: '0.875rem',
                      color: 'var(--gray-600)',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    Pagina {page}
                  </span>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={users.length < 20}
                  >
                    Successiva
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
